import React, { useContext, useEffect, useState } from 'react'
import { Box, Button, Collapse, IconButton, MenuItem, Paper, Stack, Tooltip, TextField, Typography } from '@mui/material'
import { MdExpandMore } from "react-icons/md";
import { commaMoneyFormat, formatDate, frequencyOptions } from '../../../Utils';
import { CaseContext } from '../../../context/CaseContext';
import { PaymentPlanContext } from '../../../context/PaymentPlanContext';


const EditPaymentPlanModal = (props) => {

    const {
        editPaymentPlanModal,
        setEditPaymentPlanModal,
        toast,
        updateBalanceRows,
        updateCaseBalance,
        violationRows,
        violationView,
    } = props;

    const { vioTransactionTbl } = useContext(CaseContext);

    const {
        paymentPlanFrequencyList,
        paymentPlanPeriodicityList,
        paymentPlanTypes,
        handlePaymentPlanFieldChange,
        handleResetPaymentPlanState,
        paymentPlanState,
        createPaymentPlan,
        retrievePaymentPlanByCaseId,
        paymentPlanTemplates,
        frequencyDetails,
        setFrequencyDetails,
        updatePaymentPlan,
        updatePlanModal,
        setUpdatePlanModal,
        installments,
    } = useContext(PaymentPlanContext);

    const [collapse,] = useState('COLLAPSE');
    const [expand,] = useState('EXPAND');

    function handleClose() {
        setEditPaymentPlanModal(false);
        //setUpdatePlanModal(false);
        handleResetPaymentPlanState();
        return;
    }

    async function handleUpdatePaymentPlan(paymentPlanState, violationView) {
        try {
            await updatePaymentPlan(violationView.paymentPlan.id, paymentPlanState, violationView.fkCaseId, violationView.pkViolationId);
            await retrievePaymentPlanByCaseId(violationView.fkCaseId);
            await updateCaseBalance();
            await updateBalanceRows();
            await toast.success('PAYMENT PLAN UPDATED SUCCESSFULLY!', {
                position: 'top-right',
                autoClose: 2000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: 'colored',
            });
            setUpdatePlanModal(false);
            setEditPaymentPlanModal(false);
        }
        catch (err) {
            console.log('err: ', err);
        }
    }

    useEffect(() => {
        if (violationView && violationView.paymentPlan.isActive) {
            handlePaymentPlanFieldChange('planType', violationView.paymentPlan.paymentPlanType);
            for (const key in violationView.paymentPlan.schedule) {
                violationView.paymentPlan.schedule[key] != null
                    ? key.toLocaleLowerCase().includes('date')
                        ? handlePaymentPlanFieldChange(key, new Date(violationView.paymentPlan.schedule[key]).toISOString().substring(0, 10))
                        : key === 'totalInstallmentAmount'
                            ? handlePaymentPlanFieldChange('planAmount', new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(violationView.paymentPlan.schedule.totalInstallmentAmount))
                            : handlePaymentPlanFieldChange(key, violationView.paymentPlan.schedule[key]?.toString())
                    : delete violationView.paymentPlan.schedule[key];
            }
        }
    }, [violationView]);

    return (
        <>
            <Paper sx={{ width: 'auto', display: 'flex', height: 'auto', flexDirection: 'column', margin: 'auto', padding: 'auto' }}>
                <Paper className="border-radius-bottom" sx={{ textAlign: 'center', backgroundColor: 'steelblue', color: 'white', p: '1vh' }} elevation={10}>
                    <h1>Update Payment Plan</h1>
                </Paper>
                <Paper sx={{ p: '1vh', width: '98%', height: '50%', m: 'auto', mt: '1vh', backgroundColor: '#' }} elevation={3}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: '.5rem', height: '50%' }}>
                        <strong>
                            <p>Statute: {violationView?.currentStatute || violationView?.description}</p>
                            <p>Description: {violationView?.description || ''}</p>
                            <p>Charge Type: {violationView?.chargeType || ''}</p>
                        </strong>
                        {violationView?.amendedDate && (
                            <strong>
                                <p style={{ color: '#E3D026' }}>Violation Amended To: {violationRows?.find(i => vioTransactionTbl.amendedToViolation === i.id)?.currentStatute}</p>
                                <p style={{ color: '#E3D026' }}>Description: {violationRows?.find(i => vioTransactionTbl.amendedToViolation === i.id)?.description || ''}</p>
                                <p style={{ color: '#E3D026' }}>Charge Type: {violationRows?.find(i => vioTransactionTbl.amendedToViolation === i.id)?.chargeType || ''}</p>
                                <p style={{ color: '#E3D026' }}>Amended Date: {formatDate(violationView?.amendedDate?.split("T")[0]) || ''}</p>
                            </strong>
                        )}
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: "2rem", pr: "1rem" }}>
                            <strong style={{ textAlign: "left" }}>
                                <p>Fine Total: </p>
                                <p>Cost Total: </p>
                                <p>Fee Total: </p>
                                <p>Balance Due: </p>
                            </strong>
                            <strong>
                                <p>{commaMoneyFormat(violationView?.totalFines)}</p>
                                <p>{commaMoneyFormat(violationView?.totalCosts)}</p>
                                <p>{commaMoneyFormat(violationView?.totalFees)}</p>
                                <p>{commaMoneyFormat(violationView?.remainingBalance)}</p>
                            </strong>
                        </Box>
                    </Box>
                </Paper>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', p: 2 }}>
                    <Typography variant="h6" sx={{ mb: '1vh' }}>Select Payment Plan Template</Typography>
                    <TextField
                        id="planType"
                        name="planType"
                        label="Payment Plan Template"
                        select
                        fullWidth
                        slotProps={{
                            inputLabel: { shrink: true }
                        }}
                        value={paymentPlanState?.planType}
                        onChange={({ target }) => handlePaymentPlanFieldChange('planType', target.value)}
                    >
                        <MenuItem
                            value=''
                        >
                            N/A
                        </MenuItem>
                        {
                            paymentPlanTypes?.map((planType) => {
                                return <MenuItem
                                    key={planType.id}
                                    value={planType.id}
                                >
                                    {planType.description}
                                </MenuItem>
                            })
                        }
                    </TextField>
                    <Typography variant="h6" sx={{ mb: '.25vh', mt: '1vh' }}>Installment Conditions</Typography>

                    <Stack direction="row" spacing={2} sx={{ mt: '1rem', mb: '1rem' }}>
                        <TextField
                            id="planAmount"
                            name="planAmount"
                            label="Payment Plan Amount"
                            placeHolder="Total amount"
                            value={paymentPlanState?.planAmount}
                            fullWidth
                            slotProps={{
                                inputLabel: { shrink: true }
                            }}
                            sx={{ width: '33%' }}
                            onChange={({ target }) => handlePaymentPlanFieldChange('planAmount', target.value.replace(/[^0-9.]/g, ''))}
                            onFocus={({ target }) => handlePaymentPlanFieldChange('planAmount', target.value.replace(/^[^0-9.]/g, ''))}
                            onBlur={({ target }) => {
                                handlePaymentPlanFieldChange('planAmount',
                                    new Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        maximumFractionDigits: 2,
                                    })
                                        .format(target.value.replace(/[^0-9.]/g, '')))
                            }}
                        />
                        <TextField
                            id="planStartDate"
                            name="planStartDate"
                            label="PLAN START DATE"
                            value={paymentPlanState?.planStartDate}
                            type="date"
                            slotProps={{
                                inputLabel: { shrink: true }
                            }}
                            sx={{ width: '33%' }}
                            onChange={({ target }) => handlePaymentPlanFieldChange('planStartDate', target.value)}
                        >
                        </TextField>
                        <TextField
                            id="planEndDate"
                            name="planEndDate"
                            label="PLAN END DATE"
                            value={paymentPlanState?.planEndDate}
                            type="date"
                            slotProps={{
                                inputLabel: { shrink: true }
                            }}
                            sx={{ width: '33%' }}
                            onChange={({ target }) => handlePaymentPlanFieldChange('planEndDate', target.value)}
                        >
                        </TextField>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Typography variant="h6" sx={{ mb: '.25vh', mt: '1vh' }}>Payment Frequency Conditions</Typography>
                        <Tooltip title={`${frequencyDetails ? collapse : expand} to ${frequencyDetails ? 'HIDE' : 'VIEW'} Frequency Conditions`} placement="right">
                            <IconButton
                                onClick={() => { setFrequencyDetails(!frequencyDetails) }}
                                sx={{ transform: !frequencyDetails ? 'rotate(180deg)' : 'rotate(0deg)', ml: 'auto' }}
                            >
                                <MdExpandMore />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Collapse in={frequencyDetails} timeout="auto" unmountOnExit>
                        <Stack direction="row" spacing={2} sx={{ mt: '1rem', mb: '1rem' }}>
                            <TextField
                                id="frequencyId"
                                name="frequencyID"
                                label="Occurrences"
                                select
                                fullWidth
                                slotProps={{
                                    inputLabel: { shrink: true }
                                }}
                                value={paymentPlanState?.frequencyId}
                                onChange={({ target }) => handlePaymentPlanFieldChange('frequencyId', target.value)}
                            >
                                <MenuItem
                                    value=''
                                >
                                    N/A
                                </MenuItem>
                                {
                                    paymentPlanFrequencyList?.map((frequency) => {
                                        return <MenuItem
                                            key={frequency.id}
                                            value={frequency.id}
                                        >
                                            {frequencyOptions[frequency.frequency]}
                                        </MenuItem>
                                    })
                                }
                            </TextField>
                            <TextField
                                id="periodicityId"
                                name="periodicityId"
                                label="Payment Frequency"
                                select
                                fullWidth
                                slotProps={{
                                    inputLabel: { shrink: true }
                                }}
                                onChange={({ target }) => { handlePaymentPlanFieldChange('periodicityId', target.value) }}
                                value={paymentPlanState?.periodicityId}
                            >
                                <MenuItem
                                    value=''
                                >
                                    N/A
                                </MenuItem>
                                {
                                    paymentPlanPeriodicityList?.map((periodicity) => {
                                        return <MenuItem
                                            key={periodicity.id}
                                            value={periodicity.id}
                                        >
                                            {periodicity.periodicity}
                                        </MenuItem>
                                    })
                                }
                            </TextField>
                        </Stack>
                    </Collapse>
                </Box>
                <Box display="flex" flexDirection="row" marginLeft="auto" paddingRight="1rem" sx={{ justifyContent: 'space-between', mb: '1vh' }}>
                    <Button
                        onClick={async () => { await handleUpdatePaymentPlan(paymentPlanState, violationView) }}
                        variant="contained"
                        sx={{ backgroundColor: 'steelblue', color: 'white', marginRight: '1rem' }}
                        disabled={paymentPlanState.disabled}
                    >
                        Update Plan
                    </Button>
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        sx={{ backgroundColor: 'steelblue', color: 'white' }}
                    >
                        Cancel
                    </Button>
                </Box>
            </Paper>
        </>
    )
}

export default EditPaymentPlanModal;